import React, { useEffect, lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import PrivateRoute from 'containers/PrivateRoute';
import RouterProvider from 'providers/router';
import ErrorManagement from 'components/ErrorManagement';
import Loading from 'components/Loading';
import useCategories from 'state/categories';
import useTherapist from 'state/therapists';
import useUser from 'state/user';
import useAdmin from 'state/admin';
import useBusiness from 'state/business';
import AlertServiceProvider from 'alert';
import useAppointments from 'state/appointments';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { ThemeProvider } from '@mui/material';
import terappyTheme from './theme'; // Tema oficial Terappy
import useRequiredDocumentation from 'state/requiredDocumentation';
import useConversations from 'state/conversations';
import useMessages from 'state/messages';
import { useWindowSize } from 'providers/WindowSizeProvider';
import notificationService from 'services/notificationServiceWrapper';
import { subscribeNotificationsIfNotAlready } from 'utils/notifications';
import { getToken } from 'resources/api/auth';

// Chunk crítico — primer render (sin lazy)
import Login from 'pages/Login';
import Home from './pages/Home';

// Chunks lazy — cargados solo cuando se navega a la ruta
const Logout = lazy(() => import('pages/Logout'));
const PasswordRecovery = lazy(() => import('pages/PasswordRecovery'));
const NewPassword = lazy(() => import('pages/NewPassword'));
const Register = lazy(() => import('pages/Register'));
const RegisterTherapist = lazy(() => import('pages/RegisterTherapist'));
const Confirmation = lazy(() => import('pages/Confirmation'));
const Therapists = lazy(() => import('pages/Therapists'));
const Profile = lazy(() => import('pages/Profile'));
const Timetable = lazy(() => import('pages/Timetable'));
const Appointment = lazy(() => import('pages/Appointment'));
const Notifications = lazy(() => import('pages/Notifications'));
const Conversation = lazy(() => import('pages/Conversation'));
const Videocall = lazy(() => import('pages/Videocall'));   // webrtc-adapter chunk
const Payments = lazy(() => import('pages/Payments'));     // @stripe chunk
const Admin = lazy(() => import('pages/Admin'));           // recharts chunk
const Business = lazy(() => import('pages/Business'));     // recharts chunk
const Nom035Form = lazy(() => import('pages/Client/Nom035/Nom035Form'));

const App = () => {
    const [appointments, appointmentsDispatcher] = useAppointments();
    const [categories, categoriesDispatcher] = useCategories();
    const [conversations, conversationsDispatcher] = useConversations();
    const [messages, messagesDispatcher] = useMessages();
    const [requiredDocumentation, requiredDocumentationDispatcher] = useRequiredDocumentation();
    const [therapist, therapistDispatcher] = useTherapist();
    const [user, userDispatcher] = useUser();
    const [admin, adminDispatcher] = useAdmin();
    const [business, businessDispatcher] = useBusiness();
    const windowSize = useWindowSize();

    // Initialize Notifications API on app startup
    useEffect(() => {
        if (!getToken()) return; // Don't register push before login
        notificationService.initialize().then((success) => {
            if (success) {
                subscribeNotificationsIfNotAlready();
                console.log('✅ Notifications service initialized');
            }
        }).catch((err) => {
            console.warn('⚠️ Notification initialization warning:', err.message);
        });
    }, [user.user]);

    useEffect(()=>{
        if(windowSize.height) {
            const decorationHeight = 38;
            const headerHeight = 56;
            const inputBoxHeight = 68;
            const minMessageHeight = 44;

            const spaceForMessages = windowSize.height - decorationHeight - headerHeight - inputBoxHeight;
            if(spaceForMessages > 0) {
                const messagesThatRender = spaceForMessages/minMessageHeight;
                messagesDispatcher.setExtraMessagesToFetch(Math.ceil(messagesThatRender) + 1);
            }
        }
    }, [windowSize, messagesDispatcher]);
    
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ThemeProvider theme={terappyTheme}>
                <RouterProvider>
                    <AlertServiceProvider>
                        <ErrorManagement
                            states={{
                                appointments: { state: appointments.error, resetError: appointmentsDispatcher.resetError },
                                categories: { state: categories.error, resetError: categoriesDispatcher.resetError },
                                conversations: { state: conversations.error, resetError: conversationsDispatcher.resetError },
                                messages: { state: messages.error, resetError: messagesDispatcher.resetError },
                                requiredDocumentation: { state: requiredDocumentation.error, resetError: requiredDocumentationDispatcher.resetError },
                                therapist: { state: therapist.error, resetError: therapistDispatcher.resetError },
                                admin: { state: admin.error, resetError: adminDispatcher.resetError },
                                business: { state: business.error, resetError: businessDispatcher.resetError },
                                user: { state: user.error, resetError: userDispatcher.resetError },
                            }}
                        />
                        <Suspense fallback={<Loading />}>
                            <Switch>
                                <Route path="/login" component={Login} />
                                <Route path="/registro" component={Register} />
                                <Route path="/registro-terapeutas" component={RegisterTherapist} />
                                <Route path="/logout" component={Logout} />
                                <Route path="/recuperar" component={PasswordRecovery} />
                                <Route path="/nueva-contraseña/:token" component={NewPassword} />
                                <Route exact path="/nueva-contraseña">
                                    <Redirect to="/home" />
                                </Route>
                                <Route exact path="/">
                                    <Redirect to="/home" />
                                </Route>
                                <PrivateRoute path="/admin" component={Admin} requiredRole="admin" />
                                <PrivateRoute path="/empresas" component={Business} requiredRole="business" />
                                <PrivateRoute path="/nom035" component={Nom035Form} />
                                <PrivateRoute path="/home" component={Home} />
                                <PrivateRoute path="/terapeutas" component={Therapists} />
                                <PrivateRoute path="/perfil" component={Profile} />
                                <PrivateRoute path="/notificaciones" component={Notifications} />
                                <PrivateRoute path="/conversacion/:conversationId" component={Conversation} />
                                <PrivateRoute path="/horario" component={Timetable} />
                                <PrivateRoute path="/appointment" component={Appointment} />
                                <PrivateRoute path="/videollamada" component={Videocall} />
                                <PrivateRoute path="/pagos" component={Payments} />
                                <PrivateRoute path="/confirmacion/:token" component={Confirmation} />
                            </Switch>
                        </Suspense>
                    </AlertServiceProvider>
                </RouterProvider>
            </ThemeProvider>
        </LocalizationProvider>
    );
};
export default App;
